import { BroadviewClientError, type BroadviewApiClient } from "@broadview/api-client";
import type {
  ClaimsVolumeTrendResponse,
  ClaimsVolumeSourceChoice,
  DataConfidenceResponse,
  DashboardResponse,
  MetricValue,
} from "@broadview/api-client";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import type { PortalProblemRecordReader } from "./portal-runtime";
import { usePortalRuntime } from "./portal-runtime";

export const portalQueryKeys = {
  identity: ["portal", "identity"] as const,
  divisions: ["portal", "divisions"] as const,
  division: (tenantId: string) => ["portal", "division", tenantId] as const,
  dashboard: (tenantId: string, view: string) => ["portal", "dashboard", tenantId, view] as const,
  metric: (tenantId: string, definitionId: string, snapshot = "standalone") =>
    ["portal", "metric", tenantId, definitionId, snapshot] as const,
  claimsVolumeTrend: (tenantId: string, sourceChoice: ClaimsVolumeSourceChoice = "SOURCE_RANKED") =>
    ["portal", "claims-volume-trend", tenantId, sourceChoice] as const,
  dataConfidence: (tenantId: string) => ["portal", "data-confidence", tenantId] as const,
  top5: (tenantId: string) => ["portal", "top5", tenantId] as const,
  safetyReview: (tenantId: string) => ["portal", "safety-review", tenantId] as const,
  problemRecords: (tenantId: string) => ["portal", "problem-records", tenantId] as const,
  problemRecord: (tenantId: string, id: string) =>
    ["portal", "problem-record", tenantId, id] as const,
};

const retry = (attempt: number, error: unknown) =>
  error instanceof BroadviewClientError && !error.retryable ? false : attempt < 2;

export function identityQueryOptions(client: BroadviewApiClient) {
  return queryOptions({
    queryKey: portalQueryKeys.identity,
    queryFn: () => client.identity.me(),
    staleTime: 5 * 60_000,
    retry,
  });
}

export function divisionsQueryOptions(client: BroadviewApiClient) {
  return queryOptions({
    queryKey: portalQueryKeys.divisions,
    queryFn: () => client.identity.listTenants(),
    staleTime: 5 * 60_000,
    retry,
  });
}

export function divisionContextQueryOptions(client: BroadviewApiClient, tenantId: string) {
  return queryOptions({
    queryKey: portalQueryKeys.division(tenantId),
    queryFn: () => client.identity.getTenantContext(tenantId),
    staleTime: 5 * 60_000,
    retry,
  });
}

export function dashboardQueryOptions(
  client: BroadviewApiClient,
  tenantId: string,
  view = "warranty-workbench",
) {
  return queryOptions({
    queryKey: portalQueryKeys.dashboard(tenantId, view),
    queryFn: () => client.dashboard.get({ tenantId, view }),
    staleTime: 60_000,
    retry,
  });
}

const jsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);

const governedMetricPayloadSchema = z
  .object({
    tenantId: z.string().trim().min(1).max(200),
    definitionId: z.string().trim().min(1).max(100),
    definitionVersion: z.string().trim().min(1).max(100),
    label: z.string().trim().min(1).max(200),
    value: z.union([z.number().finite(), z.string().trim().min(1), z.null()]),
    unit: z.string().trim().min(1).max(50).optional(),
    displayValue: z.string().trim().min(1).max(200),
    asOf: z.string().datetime({ offset: true }),
    dataState: z.enum(["LIVE", "DERIVED", "PROXY", "UNAVAILABLE", "SYNTHETIC"]),
    freshness: z.enum(["CURRENT", "STALE", "UNKNOWN"]),
    quality: z.enum(["VERIFIED", "PARTIAL", "ESTIMATED", "INSUFFICIENT"]),
    reconciliation: z.enum(["PASSED", "EXCEPTION", "NOT_APPLICABLE", "UNKNOWN"]),
    financialClassification: z
      .enum([
        "ACTUAL",
        "ACCRUED_RESERVED",
        "FORECAST",
        "ESTIMATED_EXPOSURE",
        "POTENTIAL_AVOIDED_EXPOSURE",
        "AWAITING_DATA",
      ])
      .optional(),
    confidence: z.number().min(0).max(1).optional(),
    sourceSummary: z.string().trim().min(1).max(1_000),
    lineageReference: z.string().trim().min(1).max(500).optional(),
    dimensions: z.record(jsonValueSchema),
  })
  .strict()
  .superRefine((metric, context) => {
    if (metric.dataState === "UNAVAILABLE" && metric.value !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: "Unavailable metrics must not contain a fabricated value.",
      });
    }
  });

export function parseGovernedMetricPayload(payload: unknown): MetricValue | undefined {
  const parsed = governedMetricPayloadSchema.safeParse(payload);
  return parsed.success ? (parsed.data as MetricValue) : undefined;
}

export function metricFromDashboard(
  dashboard: DashboardResponse | undefined,
  definitionId: string,
): MetricValue | undefined {
  for (const widget of dashboard?.widgets ?? []) {
    const metric = parseGovernedMetricPayload(widget.payload);
    if (metric?.definitionId === definitionId) return metric;
  }
  return undefined;
}

export function metricQueryOptions(
  client: BroadviewApiClient,
  tenantId: string,
  definitionId: string,
  dashboard?: DashboardResponse,
) {
  const snapshot = dashboard ? `${dashboard.generatedAt}:${dashboard.dataAsOf}` : "standalone";
  return queryOptions({
    queryKey: portalQueryKeys.metric(tenantId, definitionId, snapshot),
    queryFn: async () => {
      const governedWidgetMetric = metricFromDashboard(dashboard, definitionId);
      if (governedWidgetMetric) return governedWidgetMetric;
      return client.dashboard.getMetric({ tenantId, definitionId });
    },
    staleTime: 60_000,
    retry,
  });
}

export function claimsVolumeTrendQueryOptions(
  client: BroadviewApiClient,
  tenantId: string,
  sourceChoice: ClaimsVolumeSourceChoice = "SOURCE_RANKED",
) {
  return queryOptions({
    queryKey: portalQueryKeys.claimsVolumeTrend(tenantId, sourceChoice),
    queryFn: (): Promise<ClaimsVolumeTrendResponse> =>
      client.claimsVolumeTrend.get({ tenantId, sourceChoice }),
    staleTime: 60_000,
    retry,
  });
}

export function dataConfidenceQueryOptions(client: BroadviewApiClient, tenantId: string) {
  return queryOptions({
    queryKey: portalQueryKeys.dataConfidence(tenantId),
    queryFn: (): Promise<DataConfidenceResponse> => client.dataConfidence.get({ tenantId }),
    staleTime: 60_000,
    retry,
  });
}

export function top5InputsQueryOptions(client: BroadviewApiClient, tenantId: string) {
  return queryOptions({
    queryKey: portalQueryKeys.top5(tenantId),
    queryFn: () => client.top5.get({ tenantId }),
    staleTime: 60_000,
    retry,
  });
}

export function safetyReviewQueryOptions(client: BroadviewApiClient, tenantId: string) {
  return queryOptions({
    queryKey: portalQueryKeys.safetyReview(tenantId),
    queryFn: () => client.safetyReview.get({ tenantId }),
    staleTime: 60_000,
    retry,
  });
}

export function problemRecordsQueryOptions(
  reader: PortalProblemRecordReader | undefined,
  tenantId: string,
) {
  return queryOptions({
    queryKey: portalQueryKeys.problemRecords(tenantId),
    queryFn: () => {
      if (!reader) {
        throw new BroadviewClientError({
          status: 503,
          code: "LIVE_ADAPTER_DISABLED",
          message: "The Problem Record read adapter is not configured.",
        });
      }
      return reader.list(tenantId);
    },
    staleTime: 30_000,
    retry,
  });
}

export function problemRecordQueryOptions(
  reader: PortalProblemRecordReader | undefined,
  tenantId: string,
  id: string,
) {
  return queryOptions({
    queryKey: portalQueryKeys.problemRecord(tenantId, id),
    queryFn: () => {
      if (!reader) {
        throw new BroadviewClientError({
          status: 503,
          code: "LIVE_ADAPTER_DISABLED",
          message: "The Problem Record read adapter is not configured.",
        });
      }
      return reader.get(tenantId, id);
    },
    staleTime: 30_000,
    retry,
  });
}

export function useDashboardQuery(view = "warranty-workbench") {
  const { client, tenantContext } = usePortalRuntime();
  return useQuery(dashboardQueryOptions(client, tenantContext.tenantId, view));
}

export function useIdentityQuery() {
  const { client } = usePortalRuntime();
  return useQuery(identityQueryOptions(client));
}

export function useDivisionsQuery() {
  const { client } = usePortalRuntime();
  return useQuery(divisionsQueryOptions(client));
}

export function useDivisionContextQuery() {
  const { client, tenantContext } = usePortalRuntime();
  return useQuery(divisionContextQueryOptions(client, tenantContext.tenantId));
}

export function useMetricQuery(definitionId: string, dashboard?: DashboardResponse) {
  const { client, tenantContext } = usePortalRuntime();
  return useQuery(metricQueryOptions(client, tenantContext.tenantId, definitionId, dashboard));
}

export function useClaimsVolumeTrendQuery(
  sourceChoice: ClaimsVolumeSourceChoice = "SOURCE_RANKED",
) {
  const { client, tenantContext } = usePortalRuntime();
  return useQuery(claimsVolumeTrendQueryOptions(client, tenantContext.tenantId, sourceChoice));
}

export function useDataConfidenceQuery() {
  const { client, tenantContext } = usePortalRuntime();
  return useQuery(dataConfidenceQueryOptions(client, tenantContext.tenantId));
}

export function useTop5InputsQuery() {
  const { client, tenantContext } = usePortalRuntime();
  return useQuery(top5InputsQueryOptions(client, tenantContext.tenantId));
}

export function useSafetyReviewQuery() {
  const { client, tenantContext } = usePortalRuntime();
  return useQuery(safetyReviewQueryOptions(client, tenantContext.tenantId));
}

export function useLiveProblemRecordsQuery() {
  const { problemRecords, tenantContext } = usePortalRuntime();
  return useQuery(problemRecordsQueryOptions(problemRecords, tenantContext.tenantId));
}

export function useLiveProblemRecordQuery(id: string) {
  const { problemRecords, tenantContext } = usePortalRuntime();
  return useQuery(problemRecordQueryOptions(problemRecords, tenantContext.tenantId, id));
}
